import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Loader2, ArrowRight, ShieldCheck, Lock, Eye, EyeOff } from "lucide-react";
import { APP_NAME } from "@/lib/constants";
import { toast } from "sonner";

const ResetPassword = () => {
  const navigate = useNavigate();
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showPassword, setShowPassword] = useState(false);

  // Check if we have a recovery session
  useEffect(() => {
    supabase.auth.onAuthStateChange(async (event) => {
      if (event !== "PASSWORD_RECOVERY") {
        // If not in recovery mode, user shouldn't be here
        // but we'll let Supabase handle the actual update check
      }
    });
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (password !== confirmPassword) {
      setError("הסיסמאות אינן תואמות.");
      return;
    }

    if (password.length < 8) {
      setError("הסיסמה חייבת להכיל לפחות 8 תווים.");
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const { error } = await supabase.auth.updateUser({ password });
      
      if (error) throw error;

      toast.success("הסיסמה עודכנה בהצלחה!");
      navigate("/login");
    } catch (err: any) {
      setError(err.message || "אירעה שגיאה בעדכון הסיסמה.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-white flex flex-col items-center justify-center p-6" dir="rtl">
      <div className="w-full max-w-[440px] animate-in fade-in slide-in-from-bottom-4 duration-700">
        
        <div className="text-center mb-12">
          <Link to="/" className="text-2xl font-bold text-slate-900 tracking-tighter mb-8 inline-block">
            {APP_NAME}
          </Link>
          
          <div className="flex justify-center mb-6">
            <div className="w-16 h-16 bg-slate-50 rounded-2xl flex items-center justify-center border border-slate-100 shadow-sm">
              <Lock className="w-8 h-8 text-slate-900" />
            </div>
          </div>
          
          <h1 className="text-3xl font-bold text-slate-900 tracking-tight mb-3">סיסמה חדשה.</h1>
          <p className="text-slate-500 text-lg">בחר סיסמה חזקה ומאובטחת עבור החשבון שלך.</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          {error && (
            <Alert variant="destructive" className="bg-red-50 text-red-900 border-none rounded-2xl py-4">
              <AlertDescription className="font-medium">{error}</AlertDescription>
            </Alert>
          )}

          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="password" className="text-xs font-bold text-slate-400 uppercase tracking-widest mr-1">סיסמה חדשה</Label>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  minLength={8}
                  className="h-14 rounded-2xl border-slate-200 bg-slate-50/50 focus:bg-white focus:ring-4 focus:ring-blue-50 transition-all shadow-none pe-14 text-lg px-5"
                  placeholder="••••••••"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute inset-y-0 end-0 pe-5 flex items-center text-slate-400 hover:text-slate-600 transition-colors"
                >
                  {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                </button>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="confirmPassword" className="text-xs font-bold text-slate-400 uppercase tracking-widest mr-1">אימות סיסמה חדשה</Label>
              <Input
                id="confirmPassword"
                type={showPassword ? "text" : "password"}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
                className="h-14 rounded-2xl border-slate-200 bg-slate-50/50 focus:bg-white focus:ring-4 focus:ring-blue-50 transition-all shadow-none text-lg px-5"
                placeholder="••••••••"
              />
            </div>
          </div>

          <Button 
            type="submit" 
            className="w-full h-14 rounded-full bg-slate-900 hover:bg-black text-white font-bold text-lg shadow-xl shadow-slate-100 transition-all active:scale-[0.98] mt-4" 
            disabled={loading}
          >
            {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : "עדכן סיסמה"}
          </Button>
          
          <Button asChild variant="ghost" className="w-full h-12 rounded-full font-bold text-slate-500">
            <Link to="/login" className="flex items-center justify-center gap-2">
              <ArrowRight className="h-4 w-4" />
              ביטול וחזרה להתחברות
            </Link>
          </Button>
        </form>

        <div className="mt-20 text-center text-xs text-slate-400 font-medium">
          © {new Date().getFullYear()} {APP_NAME}. Your privacy is important.
        </div>
      </div>
    </div>
  );
};

export default ResetPassword;